import React, { type CSSProperties, type ReactNode } from "react";
import { CheckCircleFilled, CheckOutlined, CloseCircleFilled, CloseOutlined, ExclamationCircleFilled, InboxOutlined, InfoCircleFilled, StarFilled, UserOutlined, WarningFilled } from "@ant-design/icons";
import { cx } from "./fieldContext";
import { useUiLanguage } from "./uiText";
import { formatNumber } from "../units/format";
import { useUnitsOptional } from "../units/UnitsProvider";
const MAX_FRACTION_DIGITS = 20;
const PERCENT_FULL = 100;
const CIRCLE_DIAMETER_SMALL = 80;
const CIRCLE_DIAMETER_DEFAULT = 120;
const CIRCLE_RADIUS = 42;
const FULL_TURN = 2 * Math.PI;
const AVATAR_SIZE_SMALL = 24;
const AVATAR_SIZE_DEFAULT = 32;
const AVATAR_SIZE_LARGE = 40;

/*
 * Anzeige-Bausteine des Kits (Plan 2026-10-13-antd-ausbau, Stufe 4): Empty, Result, Badge, Statistic, Progress,
 * Timeline, Steps, Avatar, Rate, Descriptions. Schnittstelle wie Antd im tatsächlich genutzten Umfang (per Parser
 * erhoben), Optik aus dem Theme (`ui/display.css`, `--mq-theme-*`).
 */

type Tone = "default" | "success" | "processing" | "error" | "warning";

// ---------------------------------------------------------------- Empty

/** Standardbild des Leerzustands (DNA D20): Symbolkachel in der Akzentfarbe statt Illustration. */
const emptySimpleImage = (
  <span className="ck-empty-tile" aria-hidden="true">
    <InboxOutlined />
  </span>
);

export interface EmptyProps {
  description?: ReactNode | false;
  image?: ReactNode;
  imageStyle?: CSSProperties;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

function EmptyBase({ description, image, imageStyle, className, style, children }: EmptyProps) {
  return (
    <div className={cx("ck-empty", className)} style={style}>
      <div className="ck-empty-image" style={imageStyle}>{image ?? emptySimpleImage}</div>
      {description !== false ? <div className="ck-empty-description">{description ?? null}</div> : null}
      {children != null ? <div className="ck-empty-footer">{children}</div> : null}
    </div>
  );
}

/** Leerzustand (Ersatz für Antd `Empty`). */
export const Empty = Object.assign(EmptyBase, {
  PRESENTED_IMAGE_SIMPLE: emptySimpleImage,
  PRESENTED_IMAGE_DEFAULT: emptySimpleImage,
});

// ---------------------------------------------------------------- Result

export interface ResultProps {
  status?: "success" | "error" | "info" | "warning" | "404" | "403" | "500";
  title?: ReactNode;
  subTitle?: ReactNode;
  extra?: ReactNode;
  icon?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

const RESULT_ICONS: Record<string, ReactNode> = {
  success: <CheckCircleFilled />,
  error: <CloseCircleFilled />,
  info: <InfoCircleFilled />,
  warning: <WarningFilled />,
  "404": <ExclamationCircleFilled />,
  "403": <ExclamationCircleFilled />,
  "500": <CloseCircleFilled />,
};

/** Ergebnisseite (Ersatz für Antd `Result`). */
export function Result({ status = "info", title, subTitle, extra, icon, className, style, children }: ResultProps) {
  return (
    <div className={cx("ck-result", className)} data-status={status} style={style}>
      <div className="ck-result-icon" aria-hidden="true">{icon ?? RESULT_ICONS[status]}</div>
      {title != null ? <div className="ck-result-title">{title}</div> : null}
      {subTitle != null ? <div className="ck-result-subtitle">{subTitle}</div> : null}
      {extra != null ? <div className="ck-result-extra">{extra}</div> : null}
      {children != null ? <div className="ck-result-content">{children}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Badge

export interface BadgeProps {
  status?: Tone;
  text?: ReactNode;
  color?: string;
  count?: ReactNode;
  showZero?: boolean;
  overflowCount?: number;
  dot?: boolean;
  size?: "default" | "small";
  offset?: [number | string, number | string];
  title?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

const NAMED_COLORS = new Set(["blue", "green", "red", "orange", "gold", "yellow", "purple", "cyan", "magenta", "volcano", "geekblue", "lime", "pink", "gray", "grey"]);

/** Statuspunkt oder Zähler (Ersatz für Antd `Badge`). */
export function Badge({ status, text, color, count, showZero, overflowCount = 99, dot, size, offset, title, className, style, children }: BadgeProps) {
  const named = color && NAMED_COLORS.has(color) ? color : undefined;
  const dotStyle: CSSProperties | undefined = color && !named ? { background: color } : undefined;
  if (children == null && (status || color) && count == null) {
    return (
      <span className={cx("ck-badge-status", className)} style={style} title={title}>
        <span className="ck-badge-dot" data-tone={status ?? "default"} data-color={named} style={dotStyle} />
        {text != null ? <span className="ck-badge-text">{text}</span> : null}
      </span>
    );
  }
  const numeric = typeof count === "number";
  const hidden = count == null || (numeric && count === 0 && !showZero);
  const display = numeric && (count as number) > overflowCount ? `${overflowCount}+` : count;
  const offsetStyle: CSSProperties | undefined = offset ? { marginTop: offset[1], marginRight: typeof offset[0] === "number" ? -offset[0] : `calc(-1 * ${offset[0]})` } : undefined;
  return (
    <span className={cx("ck-badge", className)} style={style} title={title}>
      {children}
      {dot ? <sup className="ck-badge-count ck-badge-count--dot" data-color={named} style={{ ...dotStyle, ...offsetStyle }} /> : null}
      {!dot && !hidden ? (
        React.isValidElement(display) ? display : <sup className={cx("ck-badge-count", size === "small" && "ck-badge-count--sm", children == null && "ck-badge-count--standalone")} data-color={named} style={{ ...dotStyle, ...offsetStyle }}>{display}</sup>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------- Statistic

export interface StatisticProps {
  title?: ReactNode;
  value?: ReactNode;
  formatter?: (value: number | string) => ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  precision?: number;
  valueStyle?: CSSProperties;
  styles?: { title?: CSSProperties; content?: CSSProperties };
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Kennzahl (Ersatz für Antd `Statistic`). */
export function Statistic({ title, value, formatter, prefix, suffix, precision, valueStyle, styles, loading, className, style }: StatisticProps) {
  // Zahlen im Benutzerformat des Produkts (UnitsProvider); ohne Host die UI-Sprache.
  const units = useUnitsOptional();
  const lang = useUiLanguage();
  const shown = formatter
    ? formatter((value ?? "") as number | string)
    : typeof value === "number"
      ? formatNumber(value, units ?? { locale: lang }, precision != null ? { minimumFractionDigits: precision, maximumFractionDigits: precision } : { maximumFractionDigits: MAX_FRACTION_DIGITS })
      : value;
  return (
    <div className={cx("ck-statistic", className)} style={style}>
      {title != null ? <div className="ck-statistic-title" style={styles?.title}>{title}</div> : null}
      <div className="ck-statistic-content" style={{ ...styles?.content, ...valueStyle }}>
        {loading ? <span className="ck-statistic-skeleton" aria-hidden="true" /> : (
          <>
            {prefix != null ? <span className="ck-statistic-prefix">{prefix}</span> : null}
            <span className="ck-statistic-value">{shown}</span>
            {suffix != null ? <span className="ck-statistic-suffix">{suffix}</span> : null}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Progress

export interface ProgressProps {
  percent?: number;
  indeterminate?: boolean;
  status?: "success" | "exception" | "normal" | "active";
  showInfo?: boolean;
  format?: (percent?: number) => ReactNode;
  strokeColor?: string | Record<string, string>;
  type?: "line" | "circle" | "dashboard";
  size?: "small" | "default" | number | [number, number];
  className?: string;
  style?: CSSProperties;
  "aria-label"?: string;
}

function strokeOf(color: ProgressProps["strokeColor"]): string | undefined {
  if (!color) return undefined;
  if (typeof color === "string") return color;
  const values = Object.values(color);
  return values.length > 1 ? `linear-gradient(90deg, ${values.join(", ")})` : values[0];
}

/** Fortschrittsbalken bzw. -ring (Ersatz für Antd `Progress`). */
export function Progress({ percent = 0, indeterminate = false, status, showInfo = true, format, strokeColor, type = "line", size, className, style, ...aria }: ProgressProps) {
  const value = Math.max(0, Math.min(PERCENT_FULL, percent));
  const effectiveStatus = status ?? (value >= PERCENT_FULL ? "success" : "normal");
  const info = format ? format(value) : effectiveStatus === "exception" ? <CloseOutlined /> : effectiveStatus === "success" && type !== "line" ? <CheckOutlined /> : `${Math.round(value)}%`;
  const stroke = strokeOf(strokeColor);
  if (type === "circle" || type === "dashboard") {
    const diameter = typeof size === "number" ? size : size === "small" ? CIRCLE_DIAMETER_SMALL : CIRCLE_DIAMETER_DEFAULT;
    const radius = CIRCLE_RADIUS;
    const circumference = FULL_TURN * radius;
    return (
      <div className={cx("ck-progress-circle", className)} data-status={effectiveStatus} style={{ width: diameter, height: diameter, ...style }} role="progressbar" aria-valuenow={Math.round(value)} aria-valuemin={0} aria-valuemax={100} aria-label={aria["aria-label"]}>
        <svg viewBox="0 0 100 100" width={diameter} height={diameter} aria-hidden="true">
          <circle className="ck-progress-track" cx="50" cy="50" r={radius} fill="none" strokeWidth="8" />
          <circle className="ck-progress-arc" cx="50" cy="50" r={radius} fill="none" strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={circumference * (1 - value / 100)} transform="rotate(-90 50 50)"
            style={stroke && !stroke.startsWith("linear") ? { stroke } : undefined} />
        </svg>
        {showInfo ? <span className="ck-progress-circle-info" style={{ fontSize: Math.max(10, diameter / 5) }}>{info}</span> : null}
      </div>
    );
  }
  return (
    <div className={cx("ck-progress", size === "small" && "ck-progress--sm", className)} data-indeterminate={indeterminate || undefined} data-status={indeterminate ? "active" : effectiveStatus} style={style} role="progressbar" aria-valuenow={indeterminate ? undefined : Math.round(value)} aria-valuemin={0} aria-valuemax={100} aria-label={aria["aria-label"]}>
      <div className="ck-progress-track">
        <div className="ck-progress-bar" style={{ width: indeterminate ? undefined : `${value}%`, ...(stroke ? { background: stroke } : {}) }} />
      </div>
      {showInfo && !indeterminate ? <span className="ck-progress-info">{info}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Timeline

export interface TimelineItem {
  key?: React.Key;
  children?: ReactNode;
  content?: ReactNode;
  label?: ReactNode;
  color?: string;
  dot?: ReactNode;
}
export interface TimelineProps {
  items?: TimelineItem[];
  className?: string;
  style?: CSSProperties;
}

/** Zeitleiste (Ersatz für Antd `Timeline`). */
export function Timeline({ items = [], className, style }: TimelineProps) {
  return (
    <ol className={cx("ck-timeline", className)} style={style}>
      {items.map((item, index) => {
        const named = item.color && (NAMED_COLORS.has(item.color) || ["red", "green", "blue", "gray"].includes(item.color)) ? item.color : undefined;
        return (
          <li key={item.key ?? index} className="ck-timeline-item">
            <span className={cx("ck-timeline-dot", item.dot != null && "ck-timeline-dot--custom")} data-color={named} style={item.color && !named ? { borderColor: item.color, color: item.color } : undefined} aria-hidden="true">{item.dot}</span>
            <div className="ck-timeline-content">
              {item.label != null ? <div className="ck-timeline-label">{item.label}</div> : null}
              {item.children ?? item.content}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- Steps

export interface StepItem {
  title?: ReactNode;
  subTitle?: ReactNode;
  description?: ReactNode;
  content?: ReactNode;
  status?: "wait" | "process" | "finish" | "error";
  icon?: ReactNode;
  disabled?: boolean;
}
export interface StepsProps {
  items?: StepItem[];
  current?: number;
  onChange?: (current: number) => void;
  size?: "default" | "small";
  direction?: "horizontal" | "vertical";
  orientation?: "horizontal" | "vertical";
  responsive?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Schrittanzeige (Ersatz für Antd `Steps`); klickbar, wenn `onChange` gesetzt ist. */
export function Steps({ items = [], current = 0, onChange, size, direction, orientation, className, style }: StepsProps) {
  const vertical = (orientation ?? direction) === "vertical";
  return (
    <ol className={cx("ck-steps", vertical && "ck-steps--vertical", size === "small" && "ck-steps--sm", className)} style={style}>
      {items.map((item, index) => {
        const status = item.status ?? (index < current ? "finish" : index === current ? "process" : "wait");
        const clickable = Boolean(onChange) && !item.disabled && index !== current;
        const inner = (
          <>
            <span className="ck-steps-icon" aria-hidden="true">
              {item.icon ?? (status === "finish" ? <CheckOutlined /> : status === "error" ? <CloseOutlined /> : index + 1)}
            </span>
            <span className="ck-steps-text">
              <span className="ck-steps-title">{item.title}{item.subTitle != null ? <span className="ck-steps-subtitle">{item.subTitle}</span> : null}</span>
              {(item.description ?? item.content) != null ? <span className="ck-steps-description">{item.description ?? item.content}</span> : null}
            </span>
          </>
        );
        return (
          <li key={index} className="ck-steps-item" data-status={status} aria-current={index === current ? "step" : undefined}>
            {onChange ? (
              <button type="button" className="ck-steps-button" disabled={!clickable && index !== current} onClick={clickable ? () => onChange(index) : undefined}>{inner}</button>
            ) : <div className="ck-steps-button">{inner}</div>}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- Avatar

export interface AvatarProps {
  src?: string;
  alt?: string;
  size?: number | "small" | "default" | "large";
  shape?: "circle" | "square";
  icon?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/** Personenbild bzw. Initialen (Ersatz für Antd `Avatar`). */
export function Avatar({ src, alt = "", size = "default", shape = "circle", icon, className, style, children }: AvatarProps) {
  const px = typeof size === "number" ? size : size === "small" ? AVATAR_SIZE_SMALL : size === "large" ? AVATAR_SIZE_LARGE : AVATAR_SIZE_DEFAULT;
  return (
    <span className={cx("ck-avatar", shape === "square" && "ck-avatar--square", className)} style={{ width: px, height: px, fontSize: px / 2.2, ...style }}>
      {src ? <img src={src} alt={alt} /> : icon ?? children ?? <UserOutlined />}
    </span>
  );
}

// ---------------------------------------------------------------- Rate

export interface RateProps {
  value?: number;
  defaultValue?: number;
  count?: number;
  disabled?: boolean;
  onChange?: (value: number) => void;
  className?: string;
  style?: CSSProperties;
}

/** Sternbewertung (Ersatz für Antd `Rate`). */
export function Rate({ value, defaultValue = 0, count = 5, disabled, onChange, className, style }: RateProps) {
  const [inner, setInner] = React.useState(defaultValue);
  const current = value ?? inner;
  return (
    <span className={cx("ck-rate", disabled && "is-disabled", className)} style={style} role={disabled ? "img" : "radiogroup"} aria-label={`${current} / ${count}`}>
      {Array.from({ length: count }, (_, index) => {
        const filled = index < Math.round(current);
        return disabled ? (
          <span key={index} className={cx("ck-rate-star", filled && "is-filled")} aria-hidden="true"><StarFilled /></span>
        ) : (
          <button key={index} type="button" role="radio" aria-checked={index + 1 === current} className={cx("ck-rate-star", filled && "is-filled")}
            onClick={() => { setInner(index + 1); return onChange?.(index + 1); }}><StarFilled /></button>
        );
      })}
    </span>
  );
}

// ---------------------------------------------------------------- Descriptions

export interface DescriptionsItemProps {
  key?: React.Key;
  label?: ReactNode;
  children?: ReactNode;
  span?: number | "filled";
  labelStyle?: CSSProperties;
  contentStyle?: CSSProperties;
}
export interface DescriptionsProps {
  items?: DescriptionsItemProps[];
  column?: number | Partial<Record<"xs" | "sm" | "md" | "lg" | "xl" | "xxl", number>>;
  size?: "default" | "middle" | "small";
  bordered?: boolean;
  colon?: boolean;
  title?: ReactNode;
  extra?: ReactNode;
  labelStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  styles?: { label?: CSSProperties; content?: CSSProperties };
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

function DescriptionsItem(_props: DescriptionsItemProps): null {
  return null;
}

const BREAKPOINT_ORDER = ["xxl", "xl", "lg", "md", "sm", "xs"] as const;

/** Beschriftete Werte in Spalten (Ersatz für Antd `Descriptions`); Spaltenzahl je Breite wie bei Antd. */
function DescriptionsBase({ items, column = 3, size, bordered, colon = true, title, extra, labelStyle, contentStyle, styles, className, style, children }: DescriptionsProps) {
  const fromChildren: DescriptionsItemProps[] = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child, index) => {
      const props = child.props as DescriptionsItemProps;
      return { key: child.key ?? index, ...props };
    });
  const entries = items ?? fromChildren;
  const columns = typeof column === "number" ? column : undefined;
  const responsive = typeof column === "object" ? column : undefined;
  const cssColumns: CSSProperties = {};
  if (columns) (cssColumns as Record<string, string | number>)["--ck-desc-cols"] = columns;
  if (responsive) for (const bp of BREAKPOINT_ORDER) if (responsive[bp] != null) (cssColumns as Record<string, string | number>)[`--ck-desc-cols-${bp}`] = responsive[bp] as number;
  return (
    <div className={cx("ck-descriptions", bordered && "ck-descriptions--bordered", size === "small" && "ck-descriptions--sm", responsive && "ck-descriptions--responsive", className)} style={{ ...cssColumns, ...style }}>
      {title != null || extra != null ? (
        <div className="ck-descriptions-header">
          {title != null ? <div className="ck-descriptions-title">{title}</div> : <span />}
          {extra != null ? <div className="ck-descriptions-extra">{extra}</div> : null}
        </div>
      ) : null}
      <dl className="ck-descriptions-grid">
        {entries.map((item, index) => {
          const spanStyle: CSSProperties | undefined = item.span === "filled"
            ? { gridColumn: "1 / -1" }
            : typeof item.span === "number" && item.span > 1 ? { gridColumn: `span ${item.span}` } : undefined;
          return (
            <div key={item.key ?? index} className="ck-descriptions-item" style={spanStyle}>
              <dt className={cx("ck-descriptions-label", colon && !bordered && "ck-descriptions-label--colon")} style={{ ...labelStyle, ...styles?.label, ...item.labelStyle }}>{item.label}</dt>
              <dd className="ck-descriptions-content" style={{ ...contentStyle, ...styles?.content, ...item.contentStyle }}>{item.children}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

export const Descriptions = Object.assign(DescriptionsBase, { Item: DescriptionsItem });
